import axios from 'axios';
import Cookies from 'js-cookie';
import { tokenStorage } from '../utils/tokenStorage';
import { logger } from '../utils/logger';
import type { User, UserApiResponse } from '../types/auth';

interface RefreshResult {
  accessToken: string;
  refreshToken: string;
  user?: {
    id?: string;
    _id?: string;
    name: string;
    email?: string;
    phoneNumber: string;
    activeRole?: 'user' | 'merchant';
    merchantId?: string;
  } | null;
}

const CLIENT_APP_ID = 'my-ez-topup';

let isRefreshing = false;
let waiters: Array<{
  resolve: (value: RefreshResult) => void;
  reject: (reason?: unknown) => void;
}> = [];

export function isRefreshInProgress(): boolean {
  return isRefreshing;
}

export async function refreshTokensSingleFlight(): Promise<RefreshResult> {
  if (isRefreshing) {
    return new Promise<RefreshResult>((resolve, reject) => {
      waiters.push({ resolve, reject });
    });
  }

  const refreshToken = tokenStorage.getRefreshToken() || Cookies.get('refreshToken');
  if (!refreshToken) {
    const err = new Error('No refresh token available');
    logger.error('TokenRefresh: No refresh token available');
    throw err;
  }

  if (!navigator.onLine) {
    const err = new Error('Offline, cannot refresh');
    logger.warn('TokenRefresh: Offline, cannot refresh');
    throw err;
  }

  isRefreshing = true;
  try {
    logger.log('TokenRefresh: Making refresh request...');
    const url = `${import.meta.env.VITE_API_BASE_URL}${import.meta.env.VITE_API_V1_AUTH_REFRESH || '/api/v1/auth/refresh-token'}`;

    // Use raw axios to avoid interceptor recursion
    const resp = await axios.post(
      url,
      { refreshToken, clientApp: CLIENT_APP_ID },
      {
        timeout: 10000,
        headers: { 'X-Client-App': CLIENT_APP_ID },
      },
    );

    logger.log('TokenRefresh: Refresh response received');

    // Normalize response structure
    const data = resp.data?.data || resp.data;
    const accessToken: string | undefined = data?.accessToken;
    const newRefreshToken: string | undefined = data?.refreshToken;
    const userApi: UserApiResponse | null = (data?.user as UserApiResponse | undefined) ?? null;

    if (!accessToken || !newRefreshToken) {
      throw new Error('Invalid refresh response: missing tokens');
    }

    // Map to app User type or fallback to stored
    const stored = tokenStorage.getUserData();
    const fallbackUser: User = stored
      ? {
          id: stored.id,
          name: stored.name,
          email: stored.email,
          phoneNumber: stored.phoneNumber,
          activeRole:
            stored.activeRole === 'merchant' || stored.activeRole === 'user'
              ? (stored.activeRole as 'user' | 'merchant')
              : undefined,
          merchantId: stored.merchantId,
        }
      : { id: '', name: '', phoneNumber: '' };

    const currentUser: User = userApi
      ? {
          id: userApi.id || userApi._id || '',
          name: userApi.name,
          email: userApi.email || '',
          phoneNumber: userApi.phoneNumber,
          activeRole:
            userApi.activeRole === 'merchant' || userApi.activeRole === 'user'
              ? (userApi.activeRole as 'user' | 'merchant')
              : undefined,
          merchantId: userApi.merchantId,
        }
      : fallbackUser;

    // Persist tokens
    await tokenStorage.setTokens(accessToken, newRefreshToken, currentUser);

    // Sync cookies for compatibility
    const sharedCookieOptions = getSharedCookieOptions();
    Cookies.set('accessToken', accessToken, sharedCookieOptions);
    Cookies.set('refreshToken', newRefreshToken, sharedCookieOptions);

    const result: RefreshResult = { accessToken, refreshToken: newRefreshToken, user: currentUser };

    // Resolve queued waiters
    waiters.forEach((w) => w.resolve(result));
    waiters = [];
    isRefreshing = false;

    return result;
  } catch (error: unknown) {
    logger.error('TokenRefresh: Refresh failed', error);

    // Reject queued waiters
    waiters.forEach((w) => w.reject(error));
    waiters = [];
    isRefreshing = false;

    const axiosErr = error as Partial<{ response: { status?: number } | undefined; code?: string }>;
    const status = axiosErr.response?.status;
    const isNetworkErr =
      !axiosErr.response && (axiosErr.code === 'ERR_NETWORK' || !navigator.onLine);

    if (status === 401 || status === 403) {
      logger.warn(
        'TokenRefresh: Refresh rejected with 401/403; preserving current tokens for graceful recovery',
      );
    } else if (isNetworkErr) {
      logger.warn('TokenRefresh: Network error during refresh; preserving session');
    }

    throw error;
  }
}
const getSharedCookieOptions = () => {
  const baseOptions = {
    secure: typeof window === 'undefined' ? true : window.location.protocol === 'https:',
    sameSite: 'lax' as const,
    expires: 7,
    path: '/',
  };

  if (typeof window === 'undefined') {
    return baseOptions;
  }

  const hostname = window.location.hostname.toLowerCase();
  if (hostname === 'myeztopup.com' || hostname.endsWith('.myeztopup.com')) {
    return {
      ...baseOptions,
      domain: '.myeztopup.com',
    };
  }
  if (hostname === 'eztopup.com.mm' || hostname.endsWith('.eztopup.com.mm')) {
    return {
      ...baseOptions,
      domain: '.eztopup.com.mm',
    };
  }

  return baseOptions;
};
